import * as Types from '@aeppic/types'

/*
 * TS believes BTreeModule has a default export (which is true) and correctly
 * want to import that. NodeJS though (when just running through a TS remover
 * as ts-node (transpile) via ava does it), imports the module.exports
 * thus the types don't match. Recasting here via explicit default property
 * access to make it match
 */
import BTreeModule from 'sorted-btree'
const BTree: typeof BTreeModule = (BTreeModule as any).default ?? BTreeModule


export interface IndexInclusionFilter {
  (entry: Types.Document): boolean
}

export interface GeneralIndexOptions {
  inclusionFilter?: IndexInclusionFilter
  useBTree?: boolean // Added option to use BTree
}

export interface PrefixTextIndexOptions extends GeneralIndexOptions { // Ensure it extends GeneralIndexOptions
  type: 'prefix'
  length: number
}

export type IndexOptions = GeneralIndexOptions | PrefixTextIndexOptions // Simplified, as PrefixTextIndexOptions now includes GeneralIndexOptions fields

export type IndexMap = { [fieldPath: string]: Index };

// Utility type guard for BTree
function isBTree<K, V1, V2>(mapOrBTree: Map<K, V1> | BTreeModule<K, V2>): mapOrBTree is BTreeModule<K, V2> {
  return mapOrBTree instanceof BTree;
}

export class Index {
  private _valuesToDocumentIds: Map<any, Set<Types.DocumentId>> | BTreeModule<any, BTreeModule<Types.DocumentId>>; // Type can be Map or BTree
  private _documentIdsToValues: Map<Types.DocumentId, any[]> | BTreeModule<Types.DocumentId, any[]>;
  private _isSuperSet = false
  private _prefixLength: number = null
  private _isBTree: boolean = false

  constructor(private _fieldPath: string, private _options: IndexOptions = {}) {
    if (this._options.useBTree) {
      this._valuesToDocumentIds = new BTree<any, BTreeModule<string>>();
      this._documentIdsToValues = new BTree<Types.DocumentId, any[]>();
      this._isBTree = true
    } else {
      this._valuesToDocumentIds = new Map<any, Set<string>>();
      this._documentIdsToValues = new Map<Types.DocumentId, any[]>();
      this._isBTree = false
    }

    if ('type' in this._options) {
      switch (this._options.type) {
        case 'prefix':
          this._isSuperSet = true
          this._prefixLength = this._options.length
          break
      }
    }
  }

  get isBTree() {
    return this._isBTree
  }

  get fieldPath() {
    return this._fieldPath
  }

  get prefixLength() {
    return this._prefixLength
  }

  public get isSuperSet() {
    return this._isSuperSet
  }

  public size() {
    return this._valuesToDocumentIds.size
  }

  public entryCount() { 
    return this._documentIdsToValues.size
  }

  public addIfMatches(entry: Types.Document) {
    if (this._options.inclusionFilter) {
      if (!this._options.inclusionFilter(entry)) {
        return
      }
    }

    const fieldValue = resolveField(entry, this._fieldPath)
    
    if (fieldValue != null && (typeof fieldValue === 'string' ? fieldValue != '' : true)) {
      this._add(fieldValue, entry)
    }
  }

  has(id: string) {
    return this._documentIdsToValues.has(id)
  }
  
  private _add(value: any, entry: Types.Document) {
    const mappedValues = Array.isArray(value) ?
                            value.map(v => this._mapValue(v)) :
                            [this._mapValue(value)]

    for (const mappedValue of mappedValues) {
      this._addSingleValue(mappedValue, entry)
    }

    this._documentIdsToValues.set(entry.id, mappedValues)
  }

  private _addSingleValue(value: any, entry: Types.Document) {
    if (value == null) {
      return
    }
    
    const list = this._getEntryList(value) 

    if (this.isBTree) {
      (list as BTreeModule<any, BTreeModule<string>>).set(entry.id, null)
    } else {
      (list as any).add(entry.id)
    }
  }

  public remove(id: string) {
    const values = this._documentIdsToValues.get(id)
    this._documentIdsToValues.delete(id)

    if (values == null) {
      return
    }
    
    for (const singleMappedValue of values) {
      this._removeSingleValue(singleMappedValue, id)
    }
  }

  *all() {
    yield *this._documentIdsToValues.keys()
  }
  
  private _removeSingleValue(singleValue: any, entryId: string) {
    const directEntries = this._valuesToDocumentIds.get(singleValue);

    if (directEntries) {
      directEntries.delete(entryId)

      if (directEntries.size === 0) {
        this._valuesToDocumentIds.delete(singleValue)
      }
    }
  }

  private _getEntryList(value: any) {
    let entries = this._valuesToDocumentIds.get(value)
    
    if (!entries) {
      entries = this.isBTree ? new BTree<Types.DocumentId> : new Set<Types.DocumentId>() 
      this._valuesToDocumentIds.set(value, entries as any)
    }

    return entries
  }

  private _mapValue(value: any) {
    if (value == null ) {
      return null
    }

    if (typeof value !== 'string') {
      value = value.toString()
    }

    if ('type' in this._options) {
      switch (this._options.type) {
        case 'prefix':
          if (value.length > this._options.length) {
            return value.substr(0, this._options.length).toLowerCase()
          } else {
            return value.toLowerCase()
          }
      }
    }

    return value
  }

  public getDocumentIdsForValue(value: any): Set<Types.DocumentId>|BTreeModule<Types.DocumentId> {
    const mappedValue = this._mapValue(value)

    if (mappedValue == null) {
      return new Set()
    }

    return this._valuesToDocumentIds.get(mappedValue)
  }

  public getEntriesForRange(minValue: any, maxValue: any): Array<[key: any, entries: Set<Types.DocumentId>|BTreeModule<Types.DocumentId>]> {
    const mappedMin = this._mapValue(minValue);
    const mappedMax = this._mapValue(maxValue);
    const matchingEntriesWithKeys: Array<[key: any, entries: Set<Types.DocumentId>]> = [];

    if (isBTree(this._valuesToDocumentIds)) {
      // Use BTree's range iteration
      const entry: [any, Set<Types.DocumentId>] = [undefined, undefined]; 
      const iterator = this._valuesToDocumentIds.entries(mappedMin, entry);

      for (;;) {
        const { done } = iterator.next();
        if (done) {
          break;
        }
        const currentKey = entry[0];
        const currentSet = entry[1];

        // Stop if currentKey exceeds maxValue (inclusive range check)
        if (mappedMax != null && currentKey > mappedMax) {
          break;
        }
        
        // BTree iterator starts at or after mappedMin. Key must be <= mappedMax.
        if (currentSet && currentSet.size > 0) {
          matchingEntriesWithKeys.push([currentKey, currentSet]);
        }
      }
    } else {
      // For Map-based storage, sort keys on each call
      const sortedValues = Array.from(this._valuesToDocumentIds.keys());

      sortedValues.sort((a, b) => {
        if (a < b) return -1;
        if (a > b) return 1;
        return 0;
      });

      for (const val of sortedValues) {
        // Assuming val is already mapped if it came from _valuesToDocumentIds keys (which are mapped)
        if (val >= mappedMin && val <= mappedMax) {
          const documentIds = this._valuesToDocumentIds.get(val);
          if (documentIds && documentIds.size > 0) {
            matchingEntriesWithKeys.push([val, documentIds]);
          }
        } else if (val > mappedMax) {
          // Optimization: if current value exceeds max, subsequent values also will.
          break;
        }
      }
    }
    return matchingEntriesWithKeys;
  }
}

// Utility function to resolve a field in an object
//
// It will accept a path like 'a.b.c' and return the value of object.a.b.c

// If the value is an array, it will return an array of values for each element
// It does not support deeper arrays. 
//
// E.g. given the object:
//
// ```json
// {
//   a: 'A',
//   data: {
//     b: 'B',
//     c: 4,
//     d: {
//       id: 'E',
//       text: 'F',
//     },
//     e: [
//       { id: 'G', text: 'H' },
//       { id: 'I', text: 'J' },
//     ],
//    },
//  }
// ```
//
// Then the following calls will return:
// 
// resolveField(object, 'a') => 'A'
// resolveField(object, 'data.b') => 'B'
// resolveField(object, 'data.c') => 4
// resolveField(object, 'data.d.id') => 'E'
// resolveField(object, 'data.d.text') => 'F'
// resolveField(object, 'data.e.id') => ['G', 'I']
// 
// This is specific to the way aeppic documents and their data fields are structured
// 
function resolveField(object: object, path: string) {
  const parts = path.split('.')
  let value: any = object

  for (const part of parts) {
    if (value == null) {
      return undefined
    }

    if (Array.isArray(value)) {
      return value.map(v => v[part])
    } else {
      value = value[part]
    }
  }

  if (value === object) {
    value = undefined
  }

  return value
}

export function intersectSets(sets: Set<string>[]): string[] {
  const sortedSets = new Array<Set<string>>(sets.length)
  
  for (let i = 0; i < sets.length; i ++) {
    const set = sets[i]

    if (!set || set.size === 0) {
      return []
    }

    sortedSets[i] = set
  }
  
  if (sortedSets.length === 0) {
    return []
  }

  sortedSets.sort((a, b) => a.size - b.size)
  
  const intersectionEnumerator = intersect(sortedSets)
  return Array.from(intersectionEnumerator)
}

function intersect<T>(sets: Set<T>[]): Set<T> {
  if (sets.length === 0) {
    return 
  } else if (sets.length === 1) {
    return sets[0]
  } else {
    const set = sets.shift()
    const intersected = new Set<T>()

    for (const key of set) {
      let isInLowerSets = true

      for (const set of sets) {
        if (!set.has(key)) {
          isInLowerSets = false
          break
        }
      }

      if (isInLowerSets) {
        intersected.add(key)
      }
    }

    return intersected
  }
}
